import dayjs from "dayjs";
import { useEffect, useRef } from "react";
import { getApiError, postFileUpload } from "tm-shared/api";
import { extractBase64Content } from "tm-shared/utils";

import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useAsyncTaskQueue } from "@/hooks/useAsyncTaskQueue";
import { photoStorage } from "@/Services/PhotoStorage";
import { parseUploadedUrl } from "@/Services/UploadPhoto";
import {
    setNextPhotoUploadRetryAt,
    setPhotoUploadRetryCandidatesCount,
    setPhotoUploadRetryProgress,
} from "@/store/slices/offline";
import { isOnline } from "@/utils/network/isOnline";
import { isNeedRetry, isRetryReadyNow } from "@/utils/photo/photoUploadRetry";

const WORKER_INTERVAL_MS = 5 * 60 * 1000;
const MAX_PHOTO_AGE_DAYS = 7;
const MAX_ALLOWED_LATENCY_MS = 2000;

export const usePhotoUploadRetry = () => {
    const dispatch = useAppDispatch();

    const { nextPhotoUploadRetryAt, ignoreBackoffOnce } = useAppSelector(
        (s) => s.offline.photoUploadRetry,
    );

    const { offlineMode, requestQueueStatus } = useAppSelector(
        (s) => s.offline,
    );
    const isQueueRunning = requestQueueStatus.isRunning;

    // ref щоб runOnce завжди бачив актуальне значення
    const isQueueRunningRef = useRef(isQueueRunning);
    useEffect(() => {
        isQueueRunningRef.current = isQueueRunning;
    }, [isQueueRunning]);

    const { queue, progress } = useAsyncTaskQueue();

    const isRunningRef = useRef(false);
    const timeoutIdRef = useRef<number | null>(null);

    // controller для скасування in-flight upload
    const abortRef = useRef<AbortController | null>(null);

    // коли стартує черга-запитів або офлайн — зупиняю активні аплоади фото
    useEffect(() => {
        if (isQueueRunning || offlineMode) {
            abortRef.current?.abort();
            abortRef.current = null;
        }
    }, [isQueueRunning, offlineMode]);

    useEffect(() => {
        dispatch(setPhotoUploadRetryProgress(progress));
    }, [progress, dispatch]);

    useEffect(() => {
        const clearPlanned = () => {
            if (timeoutIdRef.current != null) {
                clearTimeout(timeoutIdRef.current);
                timeoutIdRef.current = null;
            }
        };

        const planNext = (from = dayjs()) => {
            dispatch(
                setNextPhotoUploadRetryAt({
                    time: from
                        .add(WORKER_INTERVAL_MS, "millisecond")
                        .toISOString(),
                    ignoreBackoff: false,
                }),
            );
        };

        const runOnce = async () => {
            const isGoodLatency = await isOnline(MAX_ALLOWED_LATENCY_MS);
            // не стартую, якщо: вже запущено / офлайн / працює черга-запитів
            if (
                isRunningRef.current ||
                offlineMode ||
                isQueueRunningRef.current ||
                !isGoodLatency
            ) {
                return;
            }

            isRunningRef.current = true;

            // один controller на один запуск воркера
            abortRef.current?.abort();
            abortRef.current = new AbortController();
            const signal = abortRef.current.signal;

            try {
                const now = dayjs();
                const photos = await photoStorage.getAllPhotos();
                const allCandidates = photos.filter(isNeedRetry);

                dispatch(
                    setPhotoUploadRetryCandidatesCount(allCandidates.length),
                );

                const candidates = ignoreBackoffOnce
                    ? allCandidates
                    : allCandidates.filter((p) => isRetryReadyNow(p, now));

                queue.clear();

                for (const p of candidates) {
                    queue.add("Retry photo upload", async () => {
                        const attemptAt = dayjs().toISOString();

                        const parsed = parseUploadedUrl(p.id);

                        if (!parsed) {
                            await photoStorage.deletePhoto(p.id);
                            return;
                        }

                        const { filename, marketId, middlePath } = parsed;

                        try {
                            await postFileUpload(
                                {
                                    base64Content: extractBase64Content(
                                        p.base64,
                                    ),
                                    filename,
                                    space: marketId,
                                    middlePath,
                                },
                                { axiosOverrides: { signal } },
                            );

                            // якщо це фото треба видалити повністю
                            if (p.taskComplete) {
                                await photoStorage.deletePhoto(p.id);
                            } else {
                                await photoStorage.updatePhotoInfo(p.id, {
                                    uploaded: true,
                                });
                            }
                        } catch (err) {
                            // якщо зупинили (request queue / offline / unmount) — НЕ рахую як помилку ретраю
                            if (signal.aborted) return;

                            const photoAgeDays = now.diff(
                                dayjs(p.createdAt),
                                "day",
                            );
                            if (photoAgeDays >= MAX_PHOTO_AGE_DAYS) {
                                await photoStorage.deletePhoto(p.id);
                                return;
                            }

                            const { code, message } = getApiError(err);
                            await photoStorage.updatePhotoInfo(p.id, {
                                lastUploadAt: attemptAt,
                                uploadAttempts: p.uploadAttempts + 1,
                                lastError: `${code}: ${message}`,
                            });
                        }
                    });
                }

                if (candidates.length) {
                    await queue.runSuccessively();
                }
            } finally {
                isRunningRef.current = false;

                // закінчили ран — відпускаю controller
                abortRef.current = null;

                const updated = await photoStorage.getAllPhotos();
                dispatch(
                    setPhotoUploadRetryCandidatesCount(
                        updated.filter(isNeedRetry).length,
                    ),
                );

                queue.clear();

                planNext(dayjs());
            }
        };

        const schedule = () => {
            clearPlanned();
            if (offlineMode) return;

            const now = dayjs();

            if (!nextPhotoUploadRetryAt) {
                planNext(now);
                return;
            }

            const nextAt = dayjs(nextPhotoUploadRetryAt);
            const msLeft = Math.max(0, nextAt.diff(now, "millisecond"));

            timeoutIdRef.current = window.setTimeout(runOnce, msLeft);
        };

        schedule();

        return () => {
            clearPlanned();
            abortRef.current?.abort();
            abortRef.current = null;
        };
    }, [
        queue,
        dispatch,
        nextPhotoUploadRetryAt,
        offlineMode,
        ignoreBackoffOnce,
    ]);
};
